import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { showErrorToast } from '../../../hooks/useToast';
import { validateUrl as isSafeUrl } from '../../../utils/security';

interface UrlInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => void;
  title?: string;
  placeholder?: string;
  label?: string;
  validateUrl?: (url: string) => boolean;
}

/**
 * URL Input Modal - Replaces window.prompt() with a proper modal
 * Provides better UX with validation and theme support
 */
export const UrlInputModal: React.FC<UrlInputModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Enter URL',
  placeholder = 'https://example.com',
  label = 'URL',
  validateUrl
}) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  // Reset URL when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUrl('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedUrl = url.trim();
    
    if (!trimmedUrl) {
      showErrorToast('Invalid URL', 'Please enter a URL');
      return;
    }

    // Basic URL validation
    const validator = validateUrl || isSafeUrl;
    if (!validator(trimmedUrl)) {
      showErrorToast('Invalid URL', 'Please enter a valid URL');
      return;
    }

    // Basic URL format check
    try {
      // If it doesn't start with http:// or https://, add https://
      const finalUrl = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')
        ? trimmedUrl
        : `https://${trimmedUrl}`;
      
      onSubmit(finalUrl);
      onClose();
    } catch (error) {
      showErrorToast('Invalid URL', 'Please enter a valid URL');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="url-input" className="block text-sm font-medium theme-text mb-2">
            {label}
          </label>
          <input
            ref={inputRef}
            id="url-input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full px-4 py-3 theme-surface theme-text placeholder-theme-text-muted border theme-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 theme-text-muted hover:theme-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)] transition-colors"
          >
            Submit
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UrlInputModal;

